/**
 * @ken/module-accounts — frontend entry point.
 *
 * Story-page migration (ADR-0081/0082): the in-list detail Sheet is RETIRED.
 * The accounts module now mounts a list page at /accounts (matching the
 * sidebar nav) + a routed full-page story detail at accounts/$id (CONFIG
 * archetype — header + fact cards, NO hero). The account-name cell on the
 * list is a stretched <Link> to accounts/$id/overview.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { isRecordId, RecordNotFound } from "@ken/ui";
import { createRoute, notFound, redirect } from "@tanstack/react-router";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadAccountStoryTabs = () => import("./routes/story/account-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const AccountListPage = lazyScreen(
  () => import("./routes/account-list"),
  "AccountListPage",
);
const AccountStoryDetail = lazyScreen(
  () => import("./routes/story/account-story-detail"),
  "AccountStoryDetail",
);
const AccountActivityRoute = lazyScreen(
  loadAccountStoryTabs,
  "AccountActivityRoute",
);
const AccountOverviewRoute = lazyScreen(
  loadAccountStoryTabs,
  "AccountOverviewRoute",
);

// UI-06: accounts has no create surface in the cockpit today (records arrive
// through the QuickBooks connector), so of the item's two sanctioned outcomes
// this takes "create surface out of scope": `/accounts/new` and any other
// non-id segment render the shared RecordNotFound page — a named page with a
// way back, never a blank screen and never a form that cannot save.

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "accounts",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3).
    validateSearch: (raw: Record<string, unknown>): AccountListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: AccountListPage,
  });

  // UI-06: a STATIC route for the literal `new`, registered so the id route
  // below can never match it. Without this, `/accounts/new` was read as a
  // record whose id is the word "new" — a 422 and a blank page.
  const literalNewRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "accounts/new",
    component: RecordNotFound,
  });

  // ── Story-page detail (ADR-0081): parent accounts/$id shell + the routed
  //    Overview tab child + an index redirect to …/overview. ───────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "accounts/$id",
    // UI-06's second half: the id-shape guard. A segment that could never be
    // a record id stops here, BEFORE the detail read is issued — the
    // difference between a page that says "no such record" and a white page
    // with a 422 behind it. Covers every bad segment, not just `new`.
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { id?: string }).id)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    // The list's filter/search ride into the detail route so Prev/Next walks
    // the SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): AccountStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
    }),
    component: AccountStoryDetail,
  });

  // Index redirect accounts/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/accounts/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: AccountOverviewRoute,
  });

  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: AccountActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [detailIndexRoute, overviewRoute, activityRoute],
  );

  // Static before parameterised — the order the router resolves in, and the
  // order UI-04's swallowed-segment check reads.
  return [listRoute, literalNewRoute, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface AccountListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}

/** Story-detail search params (carry the list's filter/search). */
export interface AccountStorySearch {
  q?: string;
  filter_status?: string;
}
